import './styles/StarterKitPopUp.scss';

import { Card } from '@whistler-ui/k2/components/Card';
import { PaginatedSlider } from '@whistler-ui/k2/components/PaginatedSlider';
import { useClassHelper } from '@whistler-ui/k2/hooks/useClassHelper';
import { useLocalStorage } from '@whistler-ui/k2/hooks/useLocalStorage';
import { useLockBodyScroll } from '@whistler-ui/k2/hooks/useLockBodyScroll';
import { useQuery } from '@whistler-ui/k2/hooks/useQuery';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Architecture } from './pages/Architecture';
import { BetaTesting } from './pages/BetaTesting';
import { GoLive } from './pages/GoLive';
import { Introduction } from './pages/Introduction';
import { Journey } from './pages/Journey';
import { Prelude } from './pages/Prelude';
import { Prerequisites } from './pages/Prerequisites';
import { ProductionReadinessActivities } from './pages/ProductionReadinessActivities';
import { Timeline } from './pages/Timeline';

const componentName = 'c-StarterKitPopUp';

export function StarterKitPopUp() {
  const { searchParams, getSearchParams, removeSearchParams } = useQuery();
  const c = useClassHelper(componentName);
  const portalContainer = document.getElementById('modal-root');
  const stepQueryParams = getSearchParams('step');

  const [itsFirstTimeValue, _, removeFirstTime] =
    useLocalStorage('first-time-loading');

  const handleDismiss = () => {
    if (itsFirstTimeValue) {
      removeFirstTime();
    }

    removeSearchParams('step');
  };

  const shouldItRender = useMemo(() => {
    return stepQueryParams || Boolean(itsFirstTimeValue);
  }, [searchParams, itsFirstTimeValue]);

  useLockBodyScroll(Boolean(shouldItRender));

  // Closing Onboarding when you press Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (shouldItRender) {
          handleDismiss();
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [searchParams, itsFirstTimeValue]);

  return createPortal(
    <AnimatePresence>
      {shouldItRender && (
        <motion.section
          className={`${componentName} ${c(shouldItRender ? '' : '--hide')}`}
          role="alertdialog"
          tabIndex={0}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ y: 25 }}
            animate={{ y: 0 }}
            exit={{ y: 25 }}
            className={c('__container')}
          >
            <Card classNames={{ container: c('__content') }} noPadding>
              <PaginatedSlider onDismiss={handleDismiss} header="Onboarding">
                <PaginatedSlider.Page title={PaginatedSlider.const.PRELUDE}>
                  <Prelude />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="introduction">
                  <Introduction />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="architecture">
                  <Architecture />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="prerequisites">
                  <Prerequisites />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="timeline">
                  <Timeline />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="journey">
                  <Journey />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="production readiness">
                  <ProductionReadinessActivities />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="public beta test">
                  <BetaTesting />
                </PaginatedSlider.Page>
                <PaginatedSlider.Page title="go live">
                  <GoLive />
                </PaginatedSlider.Page>
              </PaginatedSlider>
            </Card>
          </motion.div>
        </motion.section>
      )}
    </AnimatePresence>,
    portalContainer,
  );
}
